import React, { useState } from 'react';
import { Mountain, Globe, BarChart3, Users, Home, Star, TrendingUp, Smartphone, MessageCircle, ArrowRight, CheckCircle, AlertTriangle, Banknote, Eye, Search, Code, Download } from 'lucide-react';

const pf = { fontFamily: "'Playfair Display', Georgia, serif" };

const HP_HOMESTAYS = '20,000+';
const HP_FOOTNOTE = '*Estimated based on 4,905 registered homestays (HP Economic Survey 2024-25) and 21,000+ new applications received on homestay.hp.gov.in since launch.';

const PROBLEM_STATS = [
  { label: 'Homestays in HP (registered + applied)*', value: '20,000+', sub: '*See footnote', icon: Home },
  { label: 'Pay 15-25% to OTAs per booking', value: '70%+', sub: 'Airbnb, MakeMyTrip, Booking.com', icon: Banknote },
  { label: 'Average occupancy rate', value: '~40%', sub: 'Potential: 65% with digital marketing', icon: BarChart3 },
  { label: 'Revenue leakage to OTAs annually', value: '₹500Cr+', sub: 'Estimated across HP homestay ecosystem', icon: AlertTriangle },
];

const OTA_COMPARISON = [
  { platform: 'Airbnb', commission: '17-19%', control: 'Low', data: 'No', branding: 'Airbnb' },
  { platform: 'MakeMyTrip', commission: '18-25%', control: 'Low', data: 'No', branding: 'MMT' },
  { platform: 'Booking.com', commission: '15-20%', control: 'Low', data: 'No', branding: 'Booking' },
  { platform: 'GoHimalayan', commission: '2.5%', control: 'Full', data: 'Yes — shared with Dept', branding: 'Host + HP Tourism' },
];

const SOLUTION_STEPS = [
  { step: '01', title: 'Digitize & Curate', desc: 'AI-powered content creation from basic property info. Professional descriptions, image enhancement, and verified listings — without requiring digital literacy from hosts.', icon: Globe },
  { step: '02', title: 'Market & Discover', desc: 'SEO-optimized storytelling pages, Instagram-ready creatives, and natural language search. Travellers find homestays through "riverside cottage near Manali" — not OTA algorithms.', icon: Search },
  { step: '03', title: 'Book via WhatsApp', desc: 'Zero friction. No app download, no payment gateway learning curve. Pre-filled WhatsApp messages connect travellers directly to hosts. 100% of booking value stays with the host.', icon: Smartphone },
  { step: '04', title: 'Analyze & Report', desc: 'Live dashboard for the department — valley-wise demand heatmaps, seasonal patterns, traveller demographics, occupancy tracking. Data for policy, not just marketing.', icon: BarChart3 },
];

const TIERS = [
  {
    name: 'Silver',
    price: '₹2,499',
    priceSub: '/year + 2.5% per booking',
    color: '#A8B8C8',
    features: ['License verification badge', 'Location on state map', 'AI-curated description', 'WhatsApp booking button', 'Searchable by district & valley', 'Basic performance report'],
    target: 'All 20,000+ homestays',
    listingRevenue: 20000 * 2499,
    bookingRevenue: Math.round(20000 * 18000 * 12 * 0.4 * 0.025 * 0.3), // 30% tracked, 40% occupancy, avg ₹18K/month
  },
  {
    name: 'Gold',
    price: '₹5,999',
    priceSub: '/year + 2.5% per booking',
    color: '#D4A853',
    features: ['Everything in Silver', 'Professional photo enhancement', 'Priority in search results', 'Instagram creative generation', 'Monthly performance report', 'Review management'],
    target: '~5,000 homestays (Year 1)',
    highlight: true,
    listingRevenue: 5000 * 5999,
    bookingRevenue: Math.round(5000 * 25000 * 12 * 0.5 * 0.025 * 0.5), // 50% tracked, 50% occupancy
  },
  {
    name: 'Diamond',
    price: '₹9,999',
    priceSub: '/year + 2.5% per booking',
    color: '#7BC4E0',
    features: ['Everything in Gold', 'Luxury storytelling page', 'Social media campaigns', 'Revenue analytics dashboard', 'Dedicated account manager', 'Featured in GoHimalayan campaigns'],
    target: '~1,000 homestays (Year 1)',
    listingRevenue: 1000 * 9999,
    bookingRevenue: Math.round(1000 * 35000 * 12 * 0.6 * 0.025 * 0.7), // 70% tracked, 60% occupancy
  },
];

const TOTAL_LISTING = TIERS.reduce((s, t) => s + t.listingRevenue, 0);
const TOTAL_BOOKING = TIERS.reduce((s, t) => s + t.bookingRevenue, 0);
const TOTAL_REVENUE = TOTAL_LISTING + TOTAL_BOOKING;

const IMPACT_METRICS = [
  { metric: '₹500Cr+', label: 'OTA commission savings redirected back to hosts and the state economy annually' },
  { metric: '2.5x', label: 'Projected increase in average occupancy from ~40% to 65% within 18 months of digitization' },
  { metric: '₹45K', label: 'Projected average monthly revenue per digitized homestay (up from ₹18K currently)' },
  { metric: '60,000+', label: 'Rural livelihoods sustained through direct tourism income — no middleman' },
];

const TECH_PARTNER = [
  { title: 'White-Label Portal', desc: 'Build or enhance the official HP Tourism homestay portal (homestay.hp.gov.in) with GoHimalayan\'s technology — AI content, search, analytics.', icon: Code },
  { title: 'API Integration', desc: 'Plug GoHimalayan\'s content engine, search, and booking layer into the department\'s existing digital infrastructure.', icon: Globe },
  { title: 'Analytics Dashboard', desc: 'A dedicated government dashboard with real-time data — traveller demand, valley trends, host performance, revenue tracking.', icon: BarChart3 },
  { title: 'Host Training', desc: 'On-ground digital literacy and hospitality workshops for homestay owners across all 12 districts.', icon: Users },
];

const DISTRICT_DATA = [
  { name: 'Kullu', count: 1553, pct: 31.7 },
  { name: 'Kangra', count: 939, pct: 19.1 },
  { name: 'Shimla', count: 623, pct: 12.7 },
  { name: 'Solan', count: 433, pct: 8.8 },
  { name: 'Chamba', count: 326, pct: 6.6 },
  { name: 'Mandi', count: 295, pct: 6.0 },
  { name: 'Lahaul-Spiti', count: 245, pct: 5.0 },
  { name: 'Kinnaur', count: 198, pct: 4.0 },
  { name: 'Sirmaur', count: 120, pct: 2.4 },
  { name: 'Others', count: 173, pct: 3.5 },
];

export default function PitchHimachal() {
  const [activeTier, setActiveTier] = useState(1);

  return (
    <div className="min-h-screen bg-white" data-testid="pitch-page">

      {/* Download PDF button - fixed top-right, hidden in print */}
      <div className="fixed top-6 right-6 z-50 print:hidden">
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 px-5 py-2.5 bg-white/90 backdrop-blur-md border border-stone-200 rounded-full shadow-lg text-stone-700 text-xs font-medium tracking-wider uppercase hover:bg-white hover:shadow-xl transition-all"
          data-testid="download-pdf-btn"
        >
          <Download className="w-4 h-4" />
          Download PDF
        </button>
      </div>

      {/* ═══ SLIDE 1: Title ═══ */}
      <section className="min-h-screen flex flex-col justify-center items-center px-6 relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #1a2332 0%, #2C3E50 40%, #1a4a3a 70%, #1a2332 100%)' }}>
        {/* Himachali pattern overlay */}
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 20px, white 20px, white 21px), repeating-linear-gradient(-45deg, transparent, transparent 20px, white 20px, white 21px)' }} />
        {/* Mountain silhouette */}
        <div className="absolute bottom-0 left-0 right-0 opacity-10">
          <svg viewBox="0 0 1440 300" preserveAspectRatio="none" className="w-full" fill="white">
            <path d="M0,300 L0,200 L120,180 L240,220 L360,150 L480,190 L600,120 L720,80 L840,130 L960,60 L1080,100 L1200,70 L1320,110 L1440,90 L1440,300Z" />
          </svg>
        </div>
        <div className="relative z-10 text-center max-w-4xl">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Mountain className="w-10 h-10 text-white/80" strokeWidth={2} />
            <span className="text-2xl font-bold text-white/90 tracking-tight" style={pf}>GoHimalayan</span>
          </div>
          <div className="inline-block px-4 py-1.5 rounded-full border border-white/20 bg-white/5 mb-8">
            <p className="text-white/50 text-xs tracking-[0.3em] uppercase">Technology & Marketing Partnership Proposal</p>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white font-light italic leading-tight mb-8" style={pf}>
            Empowering {HP_HOMESTAYS} Homestays.<br />Eliminating OTA Dependency.
          </h1>
          <p className="text-white/40 text-lg max-w-2xl mx-auto mb-12" style={pf}>
            A partnership proposal for the Department of Tourism, Government of Himachal Pradesh — to digitize, market, and create sustainable direct-booking channels for every licensed homestay in the state.
          </p>
          <div className="flex items-center justify-center gap-6 text-white/20 text-xs tracking-widest uppercase">
            <span>Confidential</span>
            <div className="w-1 h-1 rounded-full bg-white/20" />
            <span>April 2026</span>
            <div className="w-1 h-1 rounded-full bg-white/20" />
            <span>gohimalayan.com</span>
          </div>
        </div>
      </section>

      {/* ═══ SLIDE 2: The Real Problem — OTA Dependency ═══ */}
      <section className="py-24 px-6 bg-[#FAFAFA]">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase text-stone-400 mb-4">The Real Challenge</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-stone-900 mb-4" style={pf}>
            The homestays are online.<br />But the revenue is leaving the state.
          </h2>
          <p className="text-stone-500 text-lg max-w-3xl mb-6" style={pf}>
            With Airbnb, MakeMyTrip, and Booking.com, most HP homestays already have some digital presence. But they're paying 15-25% commission on every booking — money that leaves the host, the village, and the state economy. Meanwhile, hosts have no control over their brand, their pricing narrative, or their guest relationships.
          </p>
          <p className="text-stone-400 text-sm italic mb-16" style={pf}>
            The problem isn't visibility. It's dependency.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {PROBLEM_STATS.map((s, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-stone-100 hover:shadow-md transition-shadow">
                <s.icon className="w-6 h-6 text-[#2C5F8D] mb-4" />
                <p className="text-3xl font-bold text-stone-900 mb-1" style={pf}>{s.value}</p>
                <p className="text-sm text-stone-600 mb-1">{s.label}</p>
                <p className="text-[10px] text-stone-400">{s.sub}</p>
              </div>
            ))}
          </div>

          {/* OTA Comparison Table */}
          <div className="bg-white rounded-xl border border-stone-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-stone-100 bg-stone-50">
              <p className="text-sm font-semibold text-stone-700">Where the money goes today</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100">
                    <th className="px-6 py-3 text-left text-xs text-stone-400 uppercase tracking-wider">Platform</th>
                    <th className="px-6 py-3 text-left text-xs text-stone-400 uppercase tracking-wider">Commission</th>
                    <th className="px-6 py-3 text-left text-xs text-stone-400 uppercase tracking-wider">Host Control</th>
                    <th className="px-6 py-3 text-left text-xs text-stone-400 uppercase tracking-wider">Data shared with Dept</th>
                    <th className="px-6 py-3 text-left text-xs text-stone-400 uppercase tracking-wider">Branding</th>
                  </tr>
                </thead>
                <tbody>
                  {OTA_COMPARISON.map((row, i) => (
                    <tr key={i} className={`border-b border-stone-50 ${row.platform === 'GoHimalayan' ? 'bg-[#2C5F8D]/5' : ''}`}>
                      <td className="px-6 py-3 font-medium text-stone-800">{row.platform}</td>
                      <td className="px-6 py-3 text-stone-600">{row.commission}</td>
                      <td className="px-6 py-3 text-stone-600">{row.control}</td>
                      <td className="px-6 py-3 text-stone-600">{row.data}</td>
                      <td className="px-6 py-3 text-stone-600">{row.branding}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SLIDE 3: The Solution ═══ */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase text-stone-400 mb-4">The GoHimalayan Model</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-stone-900 mb-4" style={pf}>
            Low fixed fee. 2.5% convenience fee.<br />Hosts save 85%+ versus OTAs.
          </h2>
          <p className="text-stone-500 text-lg max-w-2xl mb-16" style={pf}>
            GoHimalayan replaces OTA dependency with a sustainable, state-aligned platform — affordable listing fees plus a small 2.5% tracked booking fee that funds the platform and creates GST visibility for the state.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            {SOLUTION_STEPS.map((s, i) => (
              <div key={i} className="flex gap-5 p-6 rounded-xl border border-stone-100 hover:shadow-md transition-shadow" style={{ borderLeft: '3px solid #2C5F8D' }}>
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-[#2C5F8D]/10 flex items-center justify-center">
                    <span className="text-[#2C5F8D] text-sm font-bold">{s.step}</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-stone-900 mb-2" style={pf}>{s.title}</h3>
                  <p className="text-sm text-stone-500 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SLIDE 4: WhatsApp Booking ═══ */}
      <section className="py-24 px-6" style={{ background: 'linear-gradient(180deg, #f0f7f4 0%, #FAFAFA 100%)' }}>
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase text-stone-400 mb-4">The Booking Model</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-stone-900 mb-4" style={pf}>
            WhatsApp-first. 2.5% not 25%.<br />The host's phone is the booking system.
          </h2>
          <p className="text-stone-500 text-lg max-w-2xl mb-16" style={pf}>
            No app to download. No 20% cut to an OTA. The traveller discovers, the host confirms, the guest arrives. 97.5% of booking value stays with the host. The 2.5% funds the platform and creates a digital record.
          </p>
          <div className="flex flex-col md:flex-row items-start gap-4">
            {[
              { step: 'Traveller discovers homestay on GoHimalayan / HP Tourism portal', icon: Globe, color: '#2C5F8D' },
              { step: '"Book via WhatsApp" — pre-filled message to host directly', icon: MessageCircle, color: '#25D366' },
              { step: 'Host confirms availability and rates. No middleman.', icon: Smartphone, color: '#2C5F8D' },
              { step: 'Guest arrives. Host earns 97.5%. Dept gets data + GST trail.', icon: CheckCircle, color: '#1a4a3a' },
            ].map((s, i) => (
              <React.Fragment key={i}>
                <div className="flex-1 bg-white rounded-xl p-6 border border-stone-100 text-center shadow-sm">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: `${s.color}15` }}>
                    <s.icon className="w-5 h-5" style={{ color: s.color }} />
                  </div>
                  <p className="text-sm text-stone-600 leading-relaxed">{s.step}</p>
                </div>
                {i < 3 && <ArrowRight className="w-5 h-5 text-stone-300 flex-shrink-0 mt-10 hidden md:block" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SLIDE 5: District-wise Data ═══ */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase text-stone-400 mb-4">State Coverage</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-stone-900 mb-4" style={pf}>
            {HP_HOMESTAYS} homestays across 12 districts.*
          </h2>
          <p className="text-stone-400 text-xs mb-12">{"*"}{HP_FOOTNOTE}</p>
          <div className="space-y-3">
            {DISTRICT_DATA.map((d, i) => (
              <div key={i} className="flex items-center gap-4">
                <span className="text-sm text-stone-600 w-28 text-right flex-shrink-0">{d.name}</span>
                <div className="flex-1 bg-stone-100 rounded-full h-8 overflow-hidden relative">
                  <div
                    className="h-full rounded-full flex items-center justify-end pr-3 transition-all duration-1000"
                    style={{ width: `${Math.max(d.pct * 2.5, 8)}%`, background: i < 3 ? 'linear-gradient(90deg, #2C5F8D, #1a4a3a)' : '#8B9DAF' }}
                  >
                    <span className="text-[11px] text-white font-medium">{d.count.toLocaleString()}</span>
                  </div>
                </div>
                <span className="text-xs text-stone-400 w-12">{d.pct}%</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SLIDE 6: Pricing Tiers ═══ */}
      <section className="py-24 px-6 bg-[#FAFAFA]">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase text-stone-400 mb-4">Annual Listing Fee Structure</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-stone-900 mb-4" style={pf}>
            Three tiers. Aligned with HP licensing categories.
          </h2>
          <p className="text-stone-500 text-lg max-w-2xl mb-16" style={pf}>
            Hybrid model: affordable annual listing fee + a small 2.5% convenience fee on tracked bookings. Compared to OTA commissions of 15-25%, hosts save over 85% — and the state gets visibility on every transaction.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {TIERS.map((t, i) => (
              <div
                key={i}
                className={`rounded-xl p-8 border-2 transition-all cursor-pointer bg-white ${activeTier === i ? 'border-[#2C5F8D] shadow-lg scale-[1.02]' : 'border-stone-100 hover:border-stone-200'}`}
                onClick={() => setActiveTier(i)}
              >
                <div className="w-3 h-3 rounded-full mb-4" style={{ backgroundColor: t.color }} />
                <h3 className="text-xl font-bold text-stone-900 mb-1" style={pf}>{t.name}</h3>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-2xl font-bold text-stone-900">{t.price}</span>
                  <span className="text-sm text-stone-400">{t.priceSub}</span>
                </div>
                <p className="text-xs text-[#2C5F8D] font-medium mb-6">{t.target}</p>
                <ul className="space-y-2.5">
                  {t.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-stone-600">
                      <CheckCircle className="w-4 h-4 text-[#2C5F8D] flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SLIDE 7: Revenue Model ═══ */}
      <section className="py-24 px-6" style={{ background: 'linear-gradient(160deg, #1a2332 0%, #1a4a3a 60%, #2C3E50 100%)' }}>
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase text-white/30 mb-4">Revenue Projection</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-white mb-4" style={pf}>
            Year 1 — Platform Revenue
          </h2>
          <p className="text-white/40 text-lg max-w-2xl mb-16" style={pf}>
            Conservative estimates. Hybrid model: annual listing fees + 2.5% convenience fee on tracked bookings. Zero hidden charges.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {TIERS.map((t, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-6 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: t.color }} />
                  <p className="text-white/50 text-sm">{t.name} Tier</p>
                </div>
                <p className="text-white text-2xl font-bold mb-1" style={pf}>{t.target.split('(')[0].trim()}</p>
                <p className="text-white/40 text-sm">{t.price}/year + 2.5% per booking</p>
                <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
                  <div className="flex justify-between">
                    <p className="text-white/30 text-xs uppercase tracking-wider">Listing Revenue</p>
                    <p className="text-white text-sm font-bold">₹{(t.listingRevenue / 100000).toFixed(1)}L</p>
                  </div>
                  <div className="flex justify-between">
                    <p className="text-white/30 text-xs uppercase tracking-wider">Booking Fee Revenue</p>
                    <p className="text-white text-sm font-bold">₹{(t.bookingRevenue / 100000).toFixed(1)}L</p>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-white/10">
                    <p className="text-white/50 text-xs uppercase tracking-wider">Total</p>
                    <p className="text-white text-base font-bold">₹{((t.listingRevenue + t.bookingRevenue) / 100000).toFixed(1)}L</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-white/10 border border-white/15 rounded-xl p-8 text-center backdrop-blur-sm">
            <p className="text-white/40 text-sm uppercase tracking-wider mb-2">Total Year 1 Platform Revenue</p>
            <p className="text-white text-5xl font-bold" style={pf}>₹{(TOTAL_REVENUE / 10000000).toFixed(2)} Cr</p>
            <div className="flex justify-center gap-8 mt-4 text-white/30 text-xs">
              <span>Listing fees: ₹{(TOTAL_LISTING / 10000000).toFixed(2)} Cr</span>
              <span>Booking fees: ₹{(TOTAL_BOOKING / 10000000).toFixed(2)} Cr</span>
            </div>
            <p className="text-white/20 text-sm mt-3">With potential for government subsidy on Silver tier for wider adoption</p>
          </div>
        </div>
      </section>

      {/* ═══ SLIDE 8: GST & State Revenue ═══ */}
      <section className="py-24 px-6" style={{ background: 'linear-gradient(180deg, #f5f0eb 0%, #FAFAFA 100%)' }}>
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase text-stone-400 mb-4">State Revenue Opportunity</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-stone-900 mb-4" style={pf}>
            Every tracked booking is<br />GST revenue for the state.
          </h2>
          <p className="text-stone-500 text-lg max-w-3xl mb-6" style={pf}>
            Today, a large share of homestay bookings happen via cash, WhatsApp, or OTAs that route payments outside the state's tax net. GoHimalayan's tracked booking model creates a digital trail for every transaction — improving GST compliance without adding burden on hosts.
          </p>
          <p className="text-stone-400 text-sm italic mb-16" style={pf}>
            HP is a special category state — GST registration threshold is just ₹10 lakh/year. Most active homestays qualify. The platform helps them comply.
          </p>

          {/* GST Rate Table */}
          <div className="bg-white rounded-xl border border-stone-100 overflow-hidden mb-12">
            <div className="px-6 py-4 border-b border-stone-100 bg-stone-50">
              <p className="text-sm font-semibold text-stone-700">GST on Homestay Bookings — Current Rates</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100">
                    <th className="px-6 py-3 text-left text-xs text-stone-400 uppercase tracking-wider">Room Rate / Night</th>
                    <th className="px-6 py-3 text-left text-xs text-stone-400 uppercase tracking-wider">GST Rate</th>
                    <th className="px-6 py-3 text-left text-xs text-stone-400 uppercase tracking-wider">State's Share (SGST)</th>
                    <th className="px-6 py-3 text-left text-xs text-stone-400 uppercase tracking-wider">HP Homestay Applicability</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-stone-50">
                    <td className="px-6 py-3 text-stone-800">Below ₹1,000</td>
                    <td className="px-6 py-3 text-stone-600">Exempt (0%)</td>
                    <td className="px-6 py-3 text-stone-600">₹0</td>
                    <td className="px-6 py-3 text-stone-400">~30% of homestays</td>
                  </tr>
                  <tr className="border-b border-stone-50 bg-[#2C5F8D]/5">
                    <td className="px-6 py-3 text-stone-800 font-medium">₹1,001 – ₹7,500</td>
                    <td className="px-6 py-3 text-stone-800 font-medium">5%</td>
                    <td className="px-6 py-3 text-stone-800 font-medium">2.5% SGST</td>
                    <td className="px-6 py-3 text-[#2C5F8D] font-medium">~65% of homestays (majority)</td>
                  </tr>
                  <tr className="border-b border-stone-50">
                    <td className="px-6 py-3 text-stone-800">Above ₹7,500</td>
                    <td className="px-6 py-3 text-stone-600">18%</td>
                    <td className="px-6 py-3 text-stone-600">9% SGST</td>
                    <td className="px-6 py-3 text-stone-400">~5% premium properties</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Revenue impact calculation */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white rounded-xl p-6 border border-stone-100">
              <p className="text-xs text-stone-400 uppercase tracking-wider mb-2">Current Scenario</p>
              <p className="text-2xl font-bold text-stone-900 mb-1" style={pf}>₹8-12Cr</p>
              <p className="text-sm text-stone-500">Estimated annual SGST from homestays (low compliance, cash bookings)</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-[#2C5F8D]/30 shadow-md">
              <p className="text-xs text-[#2C5F8D] uppercase tracking-wider mb-2">With GoHimalayan (Year 2)</p>
              <p className="text-2xl font-bold text-[#2C5F8D] mb-1" style={pf}>₹35-50Cr</p>
              <p className="text-sm text-stone-500">Projected SGST with 60% booking digitization and compliance automation</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-stone-100">
              <p className="text-xs text-stone-400 uppercase tracking-wider mb-2">State Revenue Uplift</p>
              <p className="text-2xl font-bold text-green-700 mb-1" style={pf}>3-4x</p>
              <p className="text-sm text-stone-500">Without raising any tax rate — just by improving compliance through digitization</p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-8 border border-stone-100">
            <p className="text-stone-500 text-base leading-relaxed italic" style={pf}>
              "The 2.5% convenience fee GoHimalayan charges is not a commission — it's the cost of creating a digital trail. Every tracked booking generates GST revenue for the state, improves compliance for the host, and builds a data layer the department has never had. The host still saves 85%+ versus OTA commissions."
            </p>
          </div>
        </div>
      </section>

      {/* ═══ SLIDE 9: Technology Partner ═══ */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase text-stone-400 mb-4">Beyond a Platform</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-stone-900 mb-4" style={pf}>
            GoHimalayan as your technology partner.
          </h2>
          <p className="text-stone-500 text-lg max-w-3xl mb-6" style={pf}>
            The department recently launched homestay.hp.gov.in for online registration. GoHimalayan can power the discovery, marketing, and analytics layer — either as an integrated partner or by building a white-label solution for HP Tourism's own portal.
          </p>
          <p className="text-stone-400 text-sm italic mb-16" style={pf}>
            We don't need to replace your infrastructure. We can enhance it.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {TECH_PARTNER.map((t, i) => (
              <div key={i} className="p-6 rounded-xl border border-stone-100 hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-lg bg-[#2C5F8D]/10 flex items-center justify-center mb-4">
                  <t.icon className="w-5 h-5 text-[#2C5F8D]" />
                </div>
                <h3 className="text-base font-semibold text-stone-900 mb-2">{t.title}</h3>
                <p className="text-sm text-stone-500 leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SLIDE 9: Analytics Preview ═══ */}
      <section className="py-24 px-6 bg-[#FAFAFA]">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase text-stone-400 mb-4">For the Department</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-stone-900 mb-4" style={pf}>
            A live analytics dashboard.<br />Not a quarterly PDF.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12 mb-8">
            {[
              { label: 'Traveller Searches', value: '2.9L+', sub: 'Annual (projected)', trend: '+41% YoY' },
              { label: 'Direct Bookings', value: '48,700', sub: 'Annual (projected)', trend: 'Zero commission' },
              { label: 'Target Occupancy', value: '65%', sub: 'Up from ~40% current', trend: '+25pp uplift' },
              { label: 'Revenue per Host', value: '₹45K', sub: 'Monthly average', trend: '2.5x current' },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-stone-100">
                <p className="text-xs text-stone-400 mb-2">{s.label}</p>
                <p className="text-2xl font-bold text-stone-900" style={pf}>{s.value}</p>
                <p className="text-xs text-stone-400">{s.sub}</p>
                <p className="text-xs text-[#2C5F8D] font-medium mt-2 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> {s.trend}
                </p>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-xl p-6 border border-stone-100 text-center">
            <p className="text-stone-400 text-sm mb-3">Live analytics preview</p>
            <a href="https://gohimalayan.com/states/analytics" target="_blank" rel="noopener noreferrer" className="text-[#2C5F8D] font-medium hover:underline">
              gohimalayan.com/states/analytics
            </a>
          </div>
        </div>
      </section>

      {/* ═══ SLIDE 10: Platform Traction (camouflaged) ═══ */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase text-stone-400 mb-4">Platform Readiness</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-stone-900 mb-4" style={pf}>
            Built, tested, and ready to scale.
          </h2>
          <p className="text-stone-500 text-lg max-w-2xl mb-16" style={pf}>
            GoHimalayan's technology is production-ready — the AI content pipeline, discovery engine, WhatsApp booking layer, and analytics dashboard are all operational today.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { label: 'Himalayan regions covered', value: '13 states', icon: Mountain },
              { label: 'AI-curated property descriptions', value: 'Thousands generated', icon: Star },
              { label: 'Content pipeline capacity', value: '1,000+ listings/week', icon: TrendingUp },
              { label: 'State tourism pages built', value: '13 states', icon: Globe },
              { label: 'Instagram creative engine', value: 'Automated', icon: Eye },
              { label: 'WhatsApp booking integration', value: 'Production-ready', icon: MessageCircle },
            ].map((p, i) => (
              <div key={i} className="bg-[#FAFAFA] rounded-xl p-6 border border-stone-100">
                <p.icon className="w-5 h-5 text-[#2C5F8D] mb-3" />
                <p className="text-xl font-bold text-[#2C5F8D] mb-1" style={pf}>{p.value}</p>
                <p className="text-sm text-stone-500">{p.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SLIDE 11: Impact ═══ */}
      <section className="py-24 px-6" style={{ background: 'linear-gradient(180deg, #f5f0eb 0%, #FAFAFA 100%)' }}>
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase text-stone-400 mb-4">Projected Impact</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-stone-900 mb-4" style={pf}>
            Sustainable tourism.<br />Measurable outcomes. State-level impact.
          </h2>
          <div className="grid sm:grid-cols-2 gap-8 mt-16">
            {IMPACT_METRICS.map((m, i) => (
              <div key={i} className="flex gap-5 p-6 bg-white rounded-xl border border-stone-100">
                <div className="text-3xl font-bold text-[#2C5F8D] flex-shrink-0 w-24 text-right" style={pf}>{m.metric}</div>
                <p className="text-stone-600 text-sm leading-relaxed pt-1">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SLIDE 12: The Ask ═══ */}
      <section className="py-24 px-6" style={{ background: 'linear-gradient(160deg, #1a2332 0%, #1a4a3a 60%, #2C3E50 100%)' }}>
        <div className="max-w-4xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase text-white/30 mb-6">The Partnership</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-white mb-8" style={pf}>
            A technology partnership.<br />Not a vendor contract.
          </h2>
          <div className="space-y-5 max-w-3xl mx-auto mb-16">
            {[
              'MoU designating GoHimalayan as the official technology and marketing partner for HP Tourism\'s homestay digitization initiative',
              'Access to the state homestay registry (20,000+ properties) for digital onboarding and content creation',
              'Integration with or enhancement of the official homestay.hp.gov.in portal — GoHimalayan powers the discovery and analytics layer',
              'Option for government subsidy on Silver tier listing fees — ensuring affordable entry for every licensed homestay',
              'Revenue share model on Gold and Diamond tiers — aligned incentives between platform growth and host success',
              'Quarterly review meetings with tourism department for data sharing, strategy alignment, and policy inputs',
            ].map((item, i) => (
              <div key={i} className="flex gap-4 items-start bg-white/5 rounded-lg p-4 border border-white/5">
                <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white/70 text-xs font-bold">{i + 1}</span>
                </div>
                <p className="text-white/70 text-sm leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SLIDE 13: The Bigger Vision — HP Leads the Himalayan States ═══ */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase text-stone-400 mb-4">The Bigger Opportunity</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-stone-900 mb-4" style={pf}>
            Himachal Pradesh leads.<br />12 Himalayan states follow.
          </h2>
          <p className="text-stone-500 text-lg max-w-3xl mb-6" style={pf}>
            GoHimalayan already has state pages for all 13 Himalayan states — from Ladakh to Tripura. But the platform needs a lead state partner to set the benchmark. Himachal Pradesh — with the largest homestay ecosystem, the most progressive tourism policy, and the recently launched homestay.hp.gov.in portal — is the natural first mover.
          </p>
          <p className="text-stone-500 text-lg max-w-3xl mb-16" style={pf}>
            By partnering first, HP Tourism sets the standard. The model — once proven here — becomes a replicable public-private partnership that other Himalayan states adopt under the GoHimalayan umbrella. The brand becomes co-owned: state governments provide the regulatory framework and registry access, GoHimalayan provides the technology and marketing engine.
          </p>

          <div className="bg-gradient-to-br from-[#1a2332] to-[#1a4a3a] rounded-2xl p-8 md:p-12 text-white mb-12">
            <h3 className="text-xl font-semibold mb-8 text-white/90" style={pf}>The Expansion Roadmap</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white/10 rounded-xl p-6 border border-white/10">
                <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Phase 1 — Year 1</p>
                <p className="text-white text-lg font-bold mb-2" style={pf}>Himachal Pradesh</p>
                <p className="text-white/50 text-sm">20,000+ homestays digitized. Full analytics dashboard for HP Tourism. Proof of concept established.</p>
              </div>
              <div className="bg-white/10 rounded-xl p-6 border border-white/10">
                <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Phase 2 — Year 2</p>
                <p className="text-white text-lg font-bold mb-2" style={pf}>Uttarakhand + J&K + Sikkim</p>
                <p className="text-white/50 text-sm">HP model replicated. Three more states onboarded. GoHimalayan becomes the pan-Himalayan standard.</p>
              </div>
              <div className="bg-white/10 rounded-xl p-6 border border-white/10">
                <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Phase 3 — Year 3</p>
                <p className="text-white text-lg font-bold mb-2" style={pf}>All 13 Himalayan States</p>
                <p className="text-white/50 text-sm">A unified, co-owned Himalayan hospitality platform. 1,00,000+ homestays. Public-private partnership at national scale.</p>
              </div>
            </div>
          </div>

          <div className="bg-[#FAFAFA] rounded-xl p-8 border border-stone-100">
            <p className="text-stone-500 text-base leading-relaxed italic" style={pf}>
              "The state that moves first doesn't just digitize its own homestays — it defines the model for the entire Himalayan belt. Himachal Pradesh has the ecosystem, the policy, and the infrastructure to lead. GoHimalayan has the technology. Together, this becomes India's first state-led, pan-Himalayan hospitality platform."
            </p>
          </div>
        </div>
      </section>

      {/* ═══ SLIDE 14: Close ═══ */}
      <section className="min-h-[70vh] flex flex-col justify-center items-center px-6" style={{ background: 'linear-gradient(180deg, #0d1520 0%, #1a2332 50%, #1a4a3a 100%)' }}>
        <div className="text-center max-w-3xl">
          <Mountain className="w-12 h-12 text-white/20 mx-auto mb-8" strokeWidth={1.5} />
          <h2 className="text-3xl sm:text-4xl font-light italic text-white mb-6" style={pf}>
            The mountains have always been here.<br />The homes have always welcomed.<br />Let's make the world discover them — directly.
          </h2>
          <div className="w-16 h-[1px] bg-white/15 mx-auto my-8" />
          <p className="text-white/50 text-base mb-1" style={pf}>
            GoHimalayan
          </p>
          <p className="text-white/30 text-sm tracking-wider mb-6">
            Technology & Marketing Partner for Himalayan Tourism
          </p>
          <div className="flex items-center justify-center gap-6 text-white/25 text-xs tracking-wider">
            <span>partnerships@gohimalayan.com</span>
            <div className="w-1 h-1 rounded-full bg-white/20" />
            <span>+91 9871155963</span>
          </div>
          <p className="text-white/15 text-xs tracking-wider mt-4">
            gohimalayan.com · Confidential
          </p>
        </div>
      </section>

      {/* Footnote */}
      <div className="py-6 px-6 bg-[#0d1520] border-t border-white/5">
        <p className="max-w-4xl mx-auto text-[10px] text-white/15 leading-relaxed">
          {"*"}{HP_FOOTNOTE} All projections are estimates based on industry benchmarks and GoHimalayan's operational data. Actual numbers may vary based on adoption rates, government policy, and market conditions.
        </p>
      </div>
    </div>
  );
}
